export type SamplingTechnique =
  | 'simple-random'
  | 'stratified'
  | 'systematic'
  | 'biased-convenience';

export interface SampleOptions<T> {
  strataKey?: (item: T) => string;
  biasKey?: (item: T) => number;
  random?: () => number;
}

export function sample<T>(
  population: readonly T[],
  n: number,
  technique: SamplingTechnique,
  opts: SampleOptions<T> = {},
): T[] {
  const rand = opts.random ?? Math.random;
  const N = population.length;
  if (n >= N) return [...population];

  switch (technique) {
    case 'simple-random':
      return reservoirSample(population, n, rand);
    case 'systematic':
      return systematicSample(population, n, rand);
    case 'stratified':
      if (!opts.strataKey) {
        throw new Error('sample: stratified technique requires strataKey');
      }
      return stratifiedSample(population, n, opts.strataKey, rand);
    case 'biased-convenience':
      if (!opts.biasKey) {
        throw new Error('sample: biased-convenience requires biasKey');
      }
      return biasedSample(population, n, opts.biasKey, rand);
  }
}

function reservoirSample<T>(pop: readonly T[], n: number, rand: () => number): T[] {
  const out: T[] = pop.slice(0, n);
  for (let i = n; i < pop.length; i++) {
    const j = Math.floor(rand() * (i + 1));
    if (j < n) out[j] = pop[i]!;
  }
  return out;
}

function systematicSample<T>(pop: readonly T[], n: number, rand: () => number): T[] {
  const step = Math.floor(pop.length / n);
  const start = Math.floor(rand() * step);
  const out: T[] = [];
  for (let i = 0; i < n; i++) {
    const idx = start + i * step;
    if (idx < pop.length) out.push(pop[idx]!);
  }
  return out;
}

function stratifiedSample<T>(
  pop: readonly T[],
  n: number,
  key: (item: T) => string,
  rand: () => number,
): T[] {
  const groups = new Map<string, T[]>();
  for (const item of pop) {
    const k = key(item);
    let bucket = groups.get(k);
    if (!bucket) {
      bucket = [];
      groups.set(k, bucket);
    }
    bucket.push(item);
  }
  const out: T[] = [];
  for (const [, bucket] of groups) {
    const allocation = Math.min(bucket.length, Math.round((bucket.length / pop.length) * n));
    out.push(...reservoirSample(bucket, allocation, rand));
  }
  return out;
}

function biasedSample<T>(
  pop: readonly T[],
  n: number,
  weight: (item: T) => number,
  rand: () => number,
): T[] {
  // Weighted draw without replacement via the Efraimidis–Spirakis algorithm.
  const keys = pop.map((item) => {
    const w = Math.max(weight(item), 1e-9);
    const u = rand();
    return { item, key: Math.pow(u, 1 / w) };
  });
  keys.sort((a, b) => b.key - a.key);
  return keys.slice(0, n).map((k) => k.item);
}
